'use client';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import { getBlogSidebar } from '@/utils/getBlogSidebar';
import { getBlogNav } from '@/utils/getBlogNav';

const BlogAside = dynamic(() => import('./BlogAside'), { ssr: false });
const RelatedArticles = dynamic(() => import('./RelatedArticles'), {
  ssr: false,
});

interface BlogSidebarProps {
  search: boolean;
  sharable: boolean;
  locale: string;
  data?: any;
  relatedArticles?: {
    heading: string;
    articles: any[];
  };
  showMobileRelated?: boolean;
  showDesktopRelated?: boolean;
}

const BlogSidebar = ({
  search,
  sharable,
  locale,
  data,
  relatedArticles,
  showMobileRelated = false,
  showDesktopRelated = false,
}: BlogSidebarProps) => {
  const [mounted, setMounted] = useState(false);

  const { data: sidebarData, error: sidebarError } = useSWR(
    locale ? ['blog-sidebar', locale] : null,
    () => getBlogSidebar(locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  const { data: navData, error: navError } = useSWR(
    locale ? ['blog-nav', locale] : null,
    () => getBlogNav(locale),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  const finalData = data || sidebarData;

  return (
    <>
      {showMobileRelated && relatedArticles && (
        <div className="w-full md:hidden">
          <RelatedArticles
            heading={relatedArticles.heading}
            articles={relatedArticles.articles}
          />
        </div>
      )}
      <BlogAside
        search={search}
        navData={navData}
        sharable={sharable}
        data={finalData}
      />
      {showDesktopRelated && relatedArticles && (
        <div className="w-full hidden px-4 md:block">
          <RelatedArticles
            heading={relatedArticles.heading}
            articles={relatedArticles.articles}
          />
        </div>
      )}
    </>
  );
};

export default BlogSidebar;
